import { BottomNavigation } from '@mui/material';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import {
  Children,
  cloneElement,
  isValidElement,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';

import { COACH_TOOLKIT_URL } from '@/constants';
import {
  Avatar,
  BottomBarMenuItem,
  Button,
  Icon,
  Menu,
  Typography,
} from '@/ui';

import type { User } from '@/types';
import type { BottomNavigationProps } from '@mui/material/BottomNavigation';
import type { FC, ReactNode } from 'react';

import { isCoachAppPath } from '@/utils/appContext';

type BottomBarRouteItemProps = {
  activeRoutes?: string[];
  route?: string;
  value?: string;
};

type Props = {
  menu: ReactNode;
  moreMenu: ReactNode;
  onAccountSettings: () => void;
  onFeedBack: () => void;
  onSignOut: () => void;
  renderAccountMenuItems?: (close: () => void) => ReactNode;
  user?: User;
} & BottomNavigationProps;

const BottomBar: FC<Props> = ({
  user = null,
  menu,
  moreMenu,
  onAccountSettings,
  onSignOut,
  onFeedBack,
  renderAccountMenuItems,
  ...rest
}) => {
  const location = useLocation();
  const navRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isClient = !isCoachAppPath(location.pathname);
  const path = isClient ? '/m' : '/coach';
  const activeRoute = Children.toArray(menu).find((item) => {
    if (!isValidElement<BottomBarRouteItemProps>(item)) return false;
    const { activeRoutes = [], route } = item.props;
    return Boolean(
      route &&
      (location.pathname === route ||
        activeRoutes.some((prefix) => location.pathname.startsWith(prefix))),
    );
  });
  const selectedValue = isValidElement<BottomBarRouteItemProps>(activeRoute)
    ? activeRoute.props.route
    : undefined;
  const navigationItems = Children.map(menu, (item) =>
    isValidElement<BottomBarRouteItemProps>(item) && item.props.route
      ? cloneElement(item, { value: item.props.route })
      : item,
  );

  const onOpen = () => {
    setAnchorEl(navRef.current);
  };
  const onClose = () => {
    setAnchorEl(null);
  };
  return (
    <>
      <BottomNavigation
        ref={navRef}
        className="fixed inset-x-0 bottom-0 flex h-16 w-full bg-sage-200 sm:hidden"
        value={selectedValue}
        {...rest}
      >
        {navigationItems}
        <BottomNavigationAction
          icon={<Icon>more_horiz</Icon>}
          onClick={onOpen}
        />
      </BottomNavigation>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        id="bottom-more-menu"
        open={!!anchorEl}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClick={onClose}
        onClose={onClose}
      >
        <div className="-mx-4 flex w-screen flex-col items-center border-0 border-b border-solid border-cement-100 pb-4 pt-2">
          <Avatar
            firstName={user?.firstName ?? ''}
            lastName={user?.lastName ?? ''}
            size="l"
            src={user?.avatarUrl ?? ''}
          />
          <Button
            className="absolute right-4"
            color="inherit"
            variant="text"
            onClick={onSignOut}
          >
            Sign out
          </Button>
          <Typography variant="subtitle1">{user?.firstName}</Typography>
          <Typography variant="body2">{user?.email}</Typography>
          <Link className="mt-4" to="profile">
            <Button color="secondary" variant="contained" onClick={onClose}>
              View or edit profile
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {moreMenu}
          <BottomBarMenuItem
            icon="settings"
            label="Account"
            onClick={onAccountSettings}
          />
          {renderAccountMenuItems?.(onClose)}
          <BottomBarMenuItem
            icon="feedback"
            label="Share feedback"
            onClick={onFeedBack}
          />
          <BottomBarMenuItem
            icon="assignment"
            label="Welcome guide"
            route={`${path}/welcome`}
          />
          {!isClient && (
            <BottomBarMenuItem
              icon="home_repair_service"
              label="Coach toolkit"
              route={COACH_TOOLKIT_URL}
            />
          )}
          {isClient && (
            <BottomBarMenuItem
              icon="support"
              label="FAQs"
              route={`${path}/support`}
            />
          )}
        </div>
      </Menu>
    </>
  );
};

export default BottomBar;
export type { Props };
